import { Depths, getTheme, Icon, Text, Stack } from "@fluentui/react";
import * as React from "react";
import { IRcaTest } from "../models/IRcaTest";
import { Utils } from "../Utils";
import { useStringsSanitizer } from "../hooks";
import { TrustedHtmlComponent } from "./TrustedHtmlComponent";
import { useId } from "@fluentui/react-hooks";

/**
 * The props for the component
 */
export interface IRcaTestCardProps {
  /**
   * The current test being displayed on the card
   */
  test: IRcaTest;

  /**
   * A function for navigating to the test input
   */
  navigateToTestInput: (test: IRcaTest) => void;
}

/**
 * The Test Card component on the test selection page
 *
 * @param props - the component properties
 * @returns - The test card
 */
export const RcaTestCard: React.FC<IRcaTestCardProps> = ({ test, navigateToTestInput }) => {
  const { semanticColors } = getTheme();
  const iconName = test.icon ?? "";

  const onKeyDownOrClick = (ev?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>): void => {
    // If this is a click event or an Enter keydown event where the target is not an anchor, trigger navigation.
    // Anchor (link) elements may exist inside test cards, but should not trigger test navigation
    if (ev && (ev.type === "click" || (Utils.isKeyDownEvent(ev) && ev.key === "Enter")) && !(ev.target instanceof HTMLAnchorElement)) {
      navigateToTestInput(test);
    }
  };

  const policy = useStringsSanitizer();

  const cardTitleId = useId("cardtitle");
  const cardDescriptionId = useId("carddescription");

  return (
    <Stack
      key={test.id}
      role="button"
      tabIndex={0}
      aria-labelledby={cardTitleId}
      aria-describedby={cardDescriptionId}
      onKeyDown={onKeyDownOrClick}
      onClick={onKeyDownOrClick}
      tokens={{ padding: 15, childrenGap: 12 }}
      styles={{
        root: {
          cursor: "pointer",
          maxWidth: 286,
          borderRadius: 2,
          border: "1px solid var(--rca-card-border, transparent)",
          boxShadow: `var(--rca-card-shadow, ${Depths.depth4})`,
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          backgroundColor: `var(--rca-card-background, ${semanticColors.bodyBackground})`,
          minWidth: "30%",
          height: "auto", // This is critical for making sure all the items are the same height
          selectors: {
            ":hover": {
              borderColor: "var(--rca-card-border-hover, transparent)",
              boxShadow: `var(--rca-card-shadow-hover, ${Depths.depth8})`,
            },
            ":focus-visible": {
              borderColor: "var(--rca-card-border-hover, transparent)",
              boxShadow: `var(--rca-card-shadow-hover, ${Depths.depth8})`,
            },
            [Utils.mobileQuery]: {
              maxWidth: "unset",
              width: "100%",
            },
            "@media (forced-colors: active) and (prefers-color-scheme: dark)": {
              border: "1px solid white",
            },
            "@media (forced-colors: active) and (prefers-color-scheme: light)": {
              border: "1px solid black",
            },
          },
        },
      }}
    >
      <Stack
        horizontal
        verticalAlign="center"
        styles={{
          root: {
            fontSize: 18,
            fontWeight: 400,
            margin: 0,
            padding: 0,
            height: "auto",
            width: "auto",
          },
        }}
      >
        <Icon iconName={iconName} styles={{ root: { fontSize: 32, margin: "0px 10px" } }} />
        <Text variant="mediumPlus" id={cardTitleId}>
          {test.name}
        </Text>
      </Stack>
      <Stack>
        <Text variant="small" styles={{ root: { wordWrap: "normal" } }}>
          <TrustedHtmlComponent id={cardDescriptionId} trustedHtml={policy.createHTML(test.description)} />
        </Text>
      </Stack>
    </Stack>
  );
};
